import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { STORAGE } from '../constants/storageKeys';
import { usePersistentState } from '../hooks/usePersistentState';
import './Rounds.css';

const defaultRounds = {
  round: 1,
  max: null,
  phase: '',
  history: [],
};

const defaultTimer = {
  mode: 'turn',
  durationMs: 60_000,
  status: 'idle',
  endAt: null,
  pausedRemainingMs: null,
  finishedAt: null,
  passPhone: false,
};

export default function Rounds() {
  const [rounds, setRounds] = usePersistentState(STORAGE.rounds, defaultRounds);
  const [, setTimer] = usePersistentState(STORAGE.timer, defaultTimer);
  const navigate = useNavigate();
  const roundsRef = useRef(rounds);
  roundsRef.current = rounds;

  const [undo, setUndo] = useState(null);
  const longPressRef = useRef(null);
  const plusLongFired = useRef(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpVal, setJumpVal] = useState(String(rounds.round));

  const changeRound = (delta) => {
    setRounds((x) => {
      const prev = x.round;
      const next = Math.max(0, x.round + delta);
      if (next === prev) return x;
      window.queueMicrotask(() => setUndo({ prevRound: prev, id: Date.now() }));
      return {
        ...x,
        round: next,
        history: [{ round: next, ts: Date.now() }, ...(x.history || []).slice(0, 49)],
      };
    });
  };

  const onPlusDown = () => {
    plusLongFired.current = false;
    longPressRef.current = window.setTimeout(() => {
      plusLongFired.current = true;
      setJumpVal(String(roundsRef.current.round));
      setJumpOpen(true);
    }, 500);
  };
  const onPlusUp = () => {
    if (longPressRef.current) window.clearTimeout(longPressRef.current);
  };

  useEffect(() => {
    if (!undo) return undefined;
    const t = window.setTimeout(() => setUndo(null), 5000);
    return () => window.clearTimeout(t);
  }, [undo]);

  const applyJump = () => {
    const n = Math.max(0, parseInt(jumpVal, 10) || 0);
    setRounds((x) => {
      const prev = x.round;
      if (n !== prev) window.queueMicrotask(() => setUndo({ prevRound: prev, id: Date.now() }));
      return {
        ...x,
        round: n,
        history: n !== prev ? [{ round: n, ts: Date.now() }, ...(x.history || []).slice(0, 49)] : x.history,
      };
    });
    setJumpOpen(false);
  };

  const startTurn90 = useCallback(() => {
    setTimer({
      ...defaultTimer,
      mode: 'turn',
      durationMs: 90_000,
      status: 'idle',
      endAt: null,
      pausedRemainingMs: null,
      finishedAt: null,
      passPhone: false,
    });
    navigate('/timer');
  }, [setTimer, navigate]);

  return (
    <div className="screen-panel" style={{ maxWidth: 520, margin: '0 auto' }}>
      <Link to="/" className="kit-back-link">
        ← Game Night Kit
      </Link>
      <h1 className="screen-title">Round</h1>

      <div className="round-hero" aria-live="polite">
        {rounds.round}
      </div>

      <div className="round-btns">
        <button type="button" aria-label="Decrease round" onClick={() => changeRound(-1)}>
          −
        </button>
        <button
          type="button"
          aria-label="Increase round"
          onMouseDown={onPlusDown}
          onMouseUp={onPlusUp}
          onMouseLeave={onPlusUp}
          onTouchStart={onPlusDown}
          onTouchEnd={onPlusUp}
          onClick={(e) => {
            if (plusLongFired.current) {
              plusLongFired.current = false;
              e.preventDefault();
              return;
            }
            changeRound(1);
          }}
        >
          +
        </button>
      </div>

      <label htmlFor="phase-note" style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
        Phase note (optional)
      </label>
      <input
        id="phase-note"
        className="round-phase"
        style={{ marginTop: 8 }}
        value={rounds.phase || ''}
        onChange={(e) => setRounds((x) => ({ ...x, phase: e.target.value }))}
        placeholder="e.g. Income → Actions"
        maxLength={80}
      />

      <div className="round-actions">
        <button type="button" onClick={startTurn90}>
          Start turn timer (90s)
        </button>
        <Link to="/dice" className="kit-back-link" style={{ textAlign: 'center', flex: '1 1 160px', border: '2px solid var(--accent-cyan)', borderRadius: 14, padding: 14 }}>
          Roll to break tie →
        </Link>
      </div>

      {jumpOpen && (
        <div className="modal-back" role="dialog" aria-modal="true">
          <div className="modal">
            <h2 className="screen-title" style={{ fontSize: '1.2rem' }}>
              Jump to round
            </h2>
            <input type="number" min="0" className="round-phase" value={jumpVal} onChange={(e) => setJumpVal(e.target.value)} />
            <div className="round-actions" style={{ marginTop: 12 }}>
              <button type="button" onClick={applyJump}>
                Go
              </button>
              <button type="button" onClick={() => setJumpOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {undo && (
        <div className="toast-undo">
          <span>Round changed</span>
          <button
            type="button"
            style={{ background: 'white', color: '#110022', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 800, cursor: 'pointer' }}
            onClick={() => {
              setRounds((x) => ({ ...x, round: undo.prevRound }));
              setUndo(null);
            }}
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}
